import React from 'react';
import { Table, DropdownButton, Dropdown } from 'react-bootstrap';
import { UilEllipsisV } from '@iconscout/react-unicons';
import { CarEvent } from 'providers/CarManagmentProvider';

interface MaintenanceTableProps {
  events: CarEvent[];
  sortBy: 'state' | 'startDate' | 'endDate' | null;
  sortOrder: 'asc' | 'desc' | null;
  onSort: (field: 'state' | 'startDate' | 'endDate') => void;
  onEdit: (event: CarEvent) => void;
  onDelete: (event: CarEvent) => void;
  onMarkAsResolved: (event: CarEvent) => void;
  onCreateIntervention: (event: CarEvent) => void;
}

const MaintenanceTable: React.FC<MaintenanceTableProps> = ({
  events,
  // sortBy,
  // sortOrder,
  // onSort,
  onEdit,
  onDelete,
  onMarkAsResolved,
  onCreateIntervention
}) => {
  // const getSortIcon = (field: 'state' | 'startDate' | 'endDate') => {
  //   if (sortBy !== field) return null;
  //   if (sortOrder === 'asc') return '▲';
  //   if (sortOrder === 'desc') return '▾';
  //   return null;
  // };

  const [selectedSection, setSelectedSection] = React.useState('observations');
  const observationEvents = events.filter(event => event.stateId === 2);
  const interventionEvents = events.filter(event => event.stateId !== 2);
  const interventionTypes = ['Avaria', 'Acidente', 'Manutenção', 'Inspeção'];

  const normalizeInterventionType = (state: string): string => {
    switch (state) {
      case 'avaria':
        return 'Avaria';
      case 'acidente':
        return 'Acidente';
      case 'manutencao':
        return 'Manutenção';
      case 'inspecao':
        return 'Inspeção';
      default:
        return state;
    }
  };

  const sections = [
    {
      key: 'observations',
      label: 'Observações',
      items: observationEvents
    },
    ...interventionTypes.map(type => ({
      key: `intervention-${type}`,
      label: type,
      items: interventionEvents.filter(
        event => normalizeInterventionType(event.state) === type
      )
    }))
  ];

  const activeSection =
    sections.find(section => section.key === selectedSection) ?? sections[0];

  const isInterventionTreated = (event: CarEvent): boolean => {
    return Boolean(
      event.endDate && new Date(event.endDate).getTime() < Date.now()
    );
  };

  const renderEventRow = (event: CarEvent) => (
    <tr key={event.id} className="table-row-body">
      <td className="table-cell table-cell-start-date">
        {event.startDate
          ? new Date(event.startDate).toLocaleString('pt-PT')
          : 'N/A'}
      </td>
      <td className="table-cell table-cell-end-date">
        {event.endDate
          ? new Date(event.endDate).toLocaleString('pt-PT')
          : 'N/A'}
      </td>
      <td className="table-cell table-cell-observation">
        <div>{event.observation || 'N/A'}</div>
      </td>
      <td className="table-cell table-cell-read-status">
        <span
          className={`read-status-indicator ${
            event.stateId === 2
              ? event.isResolved
                ? 'read'
                : ''
              : isInterventionTreated(event)
                ? 'read'
                : ''
          }`}
          title={
            event.stateId === 2
              ? event.isResolved
                ? 'Tratada'
                : 'Por tratar'
              : isInterventionTreated(event)
                ? 'Tratada'
                : 'Por tratar'
          }
        />
      </td>

      <td className="table-cell table-cell-action">
        <DropdownButton
          title={<UilEllipsisV size={18} aria-label="Ações" />}
          variant="outline-secondary"
          size="sm"
          className="action-dropdown"
          align="end"
        >
          {event.stateId === 2 ? (
            <>
              <Dropdown.Item onClick={() => onEdit(event)}>
                Ver detalhes
              </Dropdown.Item>
              {!event.isResolved && (
                <Dropdown.Item onClick={() => onMarkAsResolved(event)}>
                  Marcar como Tratada
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => onCreateIntervention(event)}>
                Criar Intervenção
              </Dropdown.Item>
            </>
          ) : (
            <>
              <Dropdown.Item onClick={() => onEdit(event)}>
                Editar
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => onDelete(event)}
                className="text-danger"
              >
                Eliminar
              </Dropdown.Item>
            </>
          )}
        </DropdownButton>
      </td>
    </tr>
  );

  const renderSection = (label: string, items: CarEvent[]) => {
    if (!items.length) return null;

    return (
      <>
        <tr className="table-section-divider">
          <td colSpan={5}>
            <span>{label}</span>
          </td>
        </tr>
        {items.map(renderEventRow)}
      </>
    );
  };

  return (
    <>
      <div className="table-container">
        <div className="table-section-toggle-bar" aria-label="Filtrar secções">
          {sections.map(section => (
            <button
              key={section.key}
              type="button"
              className={`table-section-toggle ${
                selectedSection === section.key ? 'active' : ''
              }`}
              onClick={() => setSelectedSection(section.key)}
            >
              <span className="table-section-label">{section.label}</span>
              <span className="table-section-count">
                {section.items.length}
              </span>
            </button>
          ))}
        </div>

        <Table size="sm" className="car-details-table maintenance-table">
          <thead className="table-header">
            <tr className="table-row-header">
              <th
                className="table-cell table-cell-start-date sortable-header"
                // onClick={() => onSort('startDate')}
              >
                Início
                {/* {getSortIcon('startDate') && (
                  <span className="sort-icon">{getSortIcon('startDate')}</span>
                )} */}
              </th>
              <th
                className="table-cell table-cell-end-date sortable-header"
                // onClick={() => onSort('endDate')}
              >
                Fim
                {/* {getSortIcon('endDate') && (
                  <span className="sort-icon">{getSortIcon('endDate')}</span>
                )} */}
              </th>
              <th className="table-cell table-cell-observation">Observações</th>
              <th className="table-cell table-cell-read-status">Tratada</th>
              <th className="table-cell table-cell-action" aria-label="Ações" />
            </tr>
          </thead>
          <tbody className="table-tbody">
            {renderSection(activeSection.label, activeSection.items)}
            {!activeSection.items.length && (
              <tr className="table-row-body empty-state-row">
                <td colSpan={5}>Nenhuma secção visível.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default MaintenanceTable;
